"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, FileUp, PhoneCall, ShieldCheck, Zap } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
export default function EnergyAdvice() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.09,
        delayChildren: shouldReduceMotion ? 0 : 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.01 : 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="independent-energy-advice" data-nav="light" className="relative overflow-hidden bg-background pt-10 pb-10 md:pt-14 md:pb-12 lg:pt-16 lg:pb-14 text-foreground">
      {/* Decorative Iso-Grid Motif (Right Side) */}
      <div aria-hidden="true" className="pointer-events-none absolute -right-16 top-1/2 hidden -translate-y-1/2 select-none opacity-20 lg:block xl:right-6">
        <div className="grid grid-cols-4 gap-3 rotate-12">
          {Array.from({
          length: 16
        }).map((_, i) => <div key={i} data-index={i} className="h-12 w-12 rounded-lg border border-primary/30 bg-primary/5 shadow-xs" />)}
        </div>
      </div>

      {/* Decorative Iso-Grid Motif (Left Side Subtle) */}
      <div aria-hidden="true" className="pointer-events-none absolute -left-20 top-12 hidden select-none opacity-15 md:block">
        <div className="grid grid-cols-3 gap-2.5 -rotate-12">
          {Array.from({
          length: 9
        }).map((_, i) => <div key={i} data-index={i} className="h-10 w-10 rounded-md border border-primary/40 bg-primary/5 shadow-xs" />)}
        </div>
      </div>

      <div className="container relative mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 items-start gap-10 lg:grid-cols-2 lg:gap-12">
          {/* Left Column: Text Content & CTAs */}
          <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
            {/* Eyebrow Status Pill */}
            <motion.div variants={itemVariants} className="mb-6">
              <Badge variant="outline" className="inline-flex items-center gap-2 rounded-lg border-border bg-card px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-foreground shadow-xs">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-lg bg-primary opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-lg bg-primary" />
                </span>
                <span>COMMERCIAL ENERGY MANAGEMENT • IRELAND</span>
              </Badge>
            </motion.div>

            {/* Primary Headline */}
            <motion.h1 variants={itemVariants} className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-5xl md:text-5xl lg:text-5xl xl:text-6xl">
              Energy Management. Real Savings.
              <span className="mt-2 block text-2xl sm:text-3xl md:text-4xl text-primary font-semibold">
                Spend Less. Save More.
              </span>
            </motion.h1>

            {/* Dual-Tone Accent Divider */}
            <motion.div variants={itemVariants} aria-hidden="true" className="my-6 h-1 w-24 rounded-lg bg-gradient-to-r from-primary via-emerald-500 to-emerald-400" />

            {/* Supporting Copy */}
            <motion.div variants={itemVariants} className="max-w-2xl space-y-4 text-base leading-relaxed text-muted-foreground sm:text-lg font-default">
              <p>
                Cost Management Ireland delivers commercial energy management, strategic procurement, and project coordination for Irish businesses. Where specialist technical services, energy audits, or capital installations are required, CMI coordinates appropriately qualified specialist contractors, SEAI Registered Energy Auditors, and technical experts rather than carrying out every specialist technical service directly.
              </p>
              <p>
                Before investing in solar, heating, lighting, or other energy upgrades, a business should first understand where the biggest energy-saving opportunities are, as the first obvious upgrade is not always the one providing the best return. CMI helps establish baseline energy performance and coordinates the necessary specialist assessments so you invest where returns are strongest.
              </p>
              <p className="text-sm font-semibold text-foreground/90 sm:text-base">
                There is no fee charged by CMI to the customer for our service.
              </p>
            </motion.div>

            {/* SEAI Callout Notice */}
            <motion.div variants={itemVariants} className="mt-6 inline-flex max-w-2xl items-center gap-2.5 rounded-xl border border-primary/30 bg-primary/10 px-4 py-2.5 text-left text-xs sm:text-sm text-foreground">
              <ShieldCheck className="h-5 w-5 shrink-0 text-primary" />
              <span>
                <strong className="font-semibold text-primary">SEAI Support:</strong> Eligible businesses may qualify for an SEAI Energy Audit Voucher worth €2,000.
              </span>
            </motion.div>

            {/* Responsive Action Cluster */}
            <motion.div variants={itemVariants} className="mt-8 flex w-full flex-wrap items-center justify-center lg:justify-start gap-3 sm:gap-4">
              {/* Primary CTA: Free Energy Review */}
              <Button asChild size="lg" className="h-12 rounded-lg bg-primary px-6 font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 transition-[background-color,box-shadow]">
                <Link to="/FreeEnergyReviewPage">
                  <span>Get a Free Energy Review</span>
                  <ArrowRight className="ml-1.5 h-4 w-4" />
                </Link>
              </Button>

              {/* Accent CTA: Check SEAI Eligibility */}
              <Button asChild size="lg" className="h-12 rounded-lg bg-emerald-600 px-6 font-semibold text-white shadow-sm hover:bg-emerald-700 transition-[background-color,box-shadow]">
                <Link to="/SeaiEnergyAuditAndEligibilityPage">
                  <Zap className="mr-1.5 h-4 w-4 text-white" />
                  <span>Check SEAI Eligibility</span>
                </Link>
              </Button>

              {/* Outlined Action: Upload a Bill */}
              <Button asChild variant="outline" size="lg" className="h-12 rounded-lg border-primary/20 bg-card px-5 font-medium text-foreground shadow-xs hover:border-primary hover:bg-primary/5 transition-[background-color,border-color]">
                <Link to="/FreeEnergyReviewPage">
                  <FileUp className="mr-1.5 h-4 w-4 text-primary" />
                  <span>Upload a Bill</span>
                </Link>
              </Button>

              {/* Outlined Action: Request a Call Back */}
              <Button asChild variant="outline" size="lg" className="h-12 rounded-lg border-primary/20 bg-card px-5 font-medium text-foreground shadow-xs hover:border-primary hover:bg-primary/5 transition-[background-color,border-color]">
                <Link to="/FreeEnergyReviewPage">
                  <PhoneCall className="mr-1.5 h-4 w-4 text-primary" />
                  <span>Request a Call Back</span>
                </Link>
              </Button>
            </motion.div>

            {/* Micro Trust Indicators */}
            <motion.div variants={itemVariants} className="mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-6 text-xs text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <Zap className="h-4 w-4 text-primary" />
                <span>Energy Procurement &amp; Management</span>
              </div>
              <div className="flex items-center gap-1.5">
                <PhoneCall className="h-4 w-4 text-primary" />
                <span>
                  Direct Support:{" "}
                  <a href="tel:040255333" className="hover:text-primary transition-colors underline-offset-2 hover:underline">
                    0402 55333
                  </a>
                </span>
              </div>
            </motion.div>
          </div>

          {/* Right Column: Hero Visual Asset with Rotating Wind Turbine Blades */}
          <motion.div variants={itemVariants} className="order-first lg:order-last relative w-full max-w-md lg:max-w-none mx-auto">
            <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-xl">
              <div className="relative aspect-[4/3] w-full overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1783268/img/CMI-turbine-background-no-blades.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1783268/img/CMI-turbine-background-no-blades.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1783268/img/CMI-turbine-background-no-blades.png-wvc-sizes" alt="Conceptual clean energy renewable infrastructure with solar panels, wind turbine and efficient power concept" loading="lazy" decoding="async" className="h-full w-full object-cover" />

                {/* Animated Wind Turbine Rotor Overlay */}
                <div aria-hidden="true" className="pointer-events-none absolute left-[45.5%] top-[25.5%] -translate-x-1/2 -translate-y-1/2 h-56 w-56 sm:h-72 sm:w-72 md:h-84 md:w-84 flex items-center justify-center">
                  <motion.div animate={shouldReduceMotion ? {
                  rotate: 0
                } : {
                  rotate: 360
                }} transition={shouldReduceMotion ? {
                  duration: 0
                } : {
                  repeat: Infinity,
                  duration: 18,
                  ease: "linear"
                }} className="h-full w-full flex items-center justify-center origin-center">
                    <svg viewBox="-120 -120 240 240" className="h-full w-full">
                      <defs>
                        <filter id="turbineBladeShadow" x="-30%" y="-30%" width="160%" height="160%">
                          <feDropShadow dx="0.8" dy="1.6" stdDeviation="1.5" floodColor="#0f172a" floodOpacity="0.25" />
                        </filter>
                        <linearGradient id="bladeSurfaceGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#ffffff" />
                          <stop offset="55%" stopColor="#f8fafc" />
                          <stop offset="100%" stopColor="#e2e8f0" />
                        </linearGradient>
                      </defs>

                      {/* 3 Aerodynamic Tapered Commercial Rotor Blades */}
                      <g filter="url(#turbineBladeShadow)">
                        {/* Blade 1 (Top) */}
                        <path d="M 0,0 C -2.5,-22 -6.5,-55 -1.2,-110 C 0.8,-110 4.5,-55 2,-22 C 0.8,-9 0,0 0,0 Z" fill="url(#bladeSurfaceGrad)" stroke="#cbd5e1" strokeWidth="0.5" />
                        {/* Blade 2 (120 deg) */}
                        <g transform="rotate(120)">
                          <path d="M 0,0 C -2.5,-22 -6.5,-55 -1.2,-110 C 0.8,-110 4.5,-55 2,-22 C 0.8,-9 0,0 0,0 Z" fill="url(#bladeSurfaceGrad)" stroke="#cbd5e1" strokeWidth="0.5" />
                        </g>
                        {/* Blade 3 (240 deg) */}
                        <g transform="rotate(240)">
                          <path d="M 0,0 C -2.5,-22 -6.5,-55 -1.2,-110 C 0.8,-110 4.5,-55 2,-22 C 0.8,-9 0,0 0,0 Z" fill="url(#bladeSurfaceGrad)" stroke="#cbd5e1" strokeWidth="0.5" />
                        </g>
                      </g>
                    </svg>
                  </motion.div>
                </div>
              </div>
              <div className="absolute inset-0 pointer-events-none rounded-2xl ring-1 ring-inset ring-black/5" />
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}